import { JwtService } from "../services/Jwt.service";

type ParamValue = string | number | boolean | Date | null | undefined;

type RequestOptions = Pick<
  RequestInit,
  "method" | "body" | "headers" | "signal"
> & {
  params?: Record<string, ParamValue | ParamValue[]>;
  skipFarmId?: boolean;
  responseType?: "blob";
  withCredentials?: boolean;
};

type HttpResponse<TData = unknown> = {
  data: TData;
  status: number;
  headers: Headers;
};

type HttpRequest = { url: string; method: string };

type TokenContainer = { id_token: string; principal: string };

class HttpError<TData = unknown> extends Error {
  constructor(
    message: string,
    readonly config: HttpRequest,
    readonly response?: HttpResponse<TData>,
  ) {
    super(message);
    this.name = "HttpError";
  }
}

let language: string | undefined;
let tokenRefresh: Promise<void> | undefined;

const setLanguage = (lang: string) => {
  language = lang.split("-")[0];
};

const customFetch = async <T>(
  url: string,
  options: RequestOptions = {},
): Promise<T> => {
  try {
    return await send<T>(url, options);
  } catch (error) {
    if (!shouldRefreshToken(error)) {
      throw error;
    }
    await refreshTokenOnce();
    return send<T>(url, options);
  }
};

const withBody =
  (method: string) =>
  <T>(url: string, body?: unknown, options: RequestOptions = {}) =>
    customFetch<HttpResponse<T>>(url, {
      ...options,
      method,
      ...serializeBody(body, options.headers),
    });

const http = {
  get: <T>(url: string, options: RequestOptions = {}) =>
    customFetch<HttpResponse<T>>(url, { ...options, method: "GET" }),
  delete: <T>(url: string, options: RequestOptions = {}) =>
    customFetch<HttpResponse<T>>(url, { ...options, method: "DELETE" }),
  post: withBody("POST"),
  put: withBody("PUT"),
  patch: withBody("PATCH"),
};

const serializeParams = (params: object = {}) => {
  const searchParams = new URLSearchParams();
  Object.entries(params).forEach(([key, value]: [string, unknown]) => {
    [value].flat().forEach((item: unknown) => {
      if (isParamValue(item)) {
        searchParams.append(key, serializeParam(item));
      }
    });
  });
  return searchParams.toString();
};

const isHttpError = <TData = unknown>(
  error: unknown,
): error is HttpError<TData> => error instanceof HttpError;

const retrieveToken = () =>
  customFetch<HttpResponse<TokenContainer>>(
    `${process.env.GATEWAY_URL}/authenticate`,
    {
      // the refresh token is set via a cookie on the gateway domain, so silent refresh doesn't work on localhost
      withCredentials: true,
      headers: JwtService.isImpersonatedToken()
        ? { "X-Expired-Authorization": JwtService.getTokenValue() ?? "" }
        : {},
    },
  );

const send = async <T>(
  url: string,
  {
    params,
    responseType,
    skipFarmId,
    withCredentials,
    ...init
  }: RequestOptions,
): Promise<T> => {
  const request = { url, method: init.method ?? "GET" };
  const response = await fetch(buildUrl(url, params, skipFarmId), {
    ...init,
    headers: buildHeaders(init.headers),
    credentials: withCredentials ? "include" : "same-origin",
  }).catch((error: unknown) => {
    if (error instanceof TypeError) {
      throw new HttpError(error.message, request);
    }
    throw error;
  });
  const result: HttpResponse<unknown> = {
    data:
      response.ok && (responseType === "blob" || isFileResponse(response))
        ? await response.blob()
        : parseJson(await response.text()),
    status: response.status,
    headers: response.headers,
  };
  if (!response.ok) {
    throw new HttpError(
      `Request failed with status code ${response.status}`,
      request,
      result,
    );
  }
  return result as T;
};

const shouldRefreshToken = (error: unknown) =>
  isHttpError(error) &&
  error.response?.status === 401 &&
  !error.config.url.includes("/api/authenticate");

const refreshTokenOnce = () => {
  tokenRefresh ??= retrieveToken()
    .then(({ data }) => {
      JwtService.storeToken(data);
      tokenRefresh = undefined;
    })
    .catch((error: unknown) => {
      JwtService.removeToken();
      window.location.href = "/login";
      throw error;
    });
  return tokenRefresh;
};

const buildHeaders = (init: HeadersInit | undefined) => {
  const headers = new Headers(init);
  if (!headers.has("Accept")) {
    headers.set("Accept", "application/json, text/plain, */*");
  }
  const token = JwtService.getTokenValue();
  if (JwtService.hasValidToken() && token) {
    headers.set("Authorization", `Bearer ${token}`);
  }
  return headers;
};

const serializeBody = (
  body: unknown,
  init: HeadersInit | undefined,
): Pick<RequestInit, "body" | "headers"> => {
  if (body === undefined) {
    return { headers: init };
  }
  if (body instanceof FormData || body instanceof Blob) {
    return { body, headers: init };
  }
  const headers = new Headers(init);
  headers.set("Content-Type", "application/json;charset=UTF-8");
  return { body: JSON.stringify(body), headers };
};

const isFileResponse = (response: Response) => {
  const type = response.headers.get("Content-Type");
  return !!type && !/json|^text\/plain/i.test(type);
};

const parseJson = (text: string): unknown => {
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
};

const buildUrl = (
  url: string,
  params: RequestOptions["params"] = {},
  skipFarmId?: boolean,
) => {
  const result = new URL(url, window.location.origin);
  const defaults = result.searchParams.has("language") ? {} : { language };
  new URLSearchParams(serializeParams({ ...defaults, ...params })).forEach(
    (value, key) => result.searchParams.append(key, value),
  );
  const farmId = getFarmIdFromLocation();
  if (farmId && !skipFarmId && !result.searchParams.has("farmIds")) {
    result.searchParams.set("farmIds", farmId);
  }
  return result.toString();
};

const isParamValue = (
  value: unknown,
): value is string | number | boolean | Date =>
  ["string", "number", "boolean"].includes(typeof value) ||
  value instanceof Date;

const serializeParam = (value: string | number | boolean | Date) =>
  value instanceof Date ? value.toISOString() : String(value);

const getFarmIdFromLocation = () => {
  const { pathname } = window.location;
  return pathname.startsWith("/farm") ? pathname.split("/")[2] : undefined;
};

export {
  http,
  customFetch,
  serializeParams,
  retrieveToken,
  setLanguage,
  HttpError,
  isHttpError,
  type HttpResponse,
  type RequestOptions,
};
